import * as Sentry from "@sentry/nextjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SEASON_CARD_DATA_KEY } from "../../../config";
import { CartContext } from "../../contexts/CartContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import {
  SEASON_CARD_ATTRIBUTES,
  SEASON_CARD_MEDIA,
  SelectedOption,
} from "../../ecommerce/product";
import { SeasonCardApplicationFields } from "../../schemas/SeasonCardApplication.schema";
import { ExtendedCartMerchandise } from "../../types/shopify-components";
import LocaleLink from "../LocaleLink";

import SeasonPassProductModal from "./SeasonPassProductModal";
import SeasonPassProductVariant from "./SeasonPassProductVariant";
import SeasonPassRenewalModal from "./SeasonPassRenewalModal";

export const TEST_ID_SEASONPASS_ACTIONS = "seasonpass-product-actions";
export const TEST_ID_SEASONPASS_OPEN_APPLICATION_MODAL =
  "seasonpass-product-actions-open-application-modal";
export const TEST_ID_BACK_TO_SHOP_BUTTON =
  "seasonpass-product-actions-back-to-shop-button";

export type SeasonCardApplicationEditData = {
  shouldOpenModal: boolean;
  cartLineId: string;
  merchandiseId: ExtendedCartMerchandise["id"];
  photoId: string;
  fields: SeasonCardApplicationFields;
};

export type ModalFn = (modal: "application" | "renewal" | null) => void;

export const defaultInitialValues: SeasonCardApplicationFields = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  guardianFirstName: "",
  guardianLastName: "",
  address: "",
  zip: "",
  city: "",
  email: "",
  phone: "",
  ticketId: "",
  termsAccepted: false,
};

type SeasonPassProductActionsProps = {
  isChangingToProductsPage: boolean;
  merchandises: ExtendedCartMerchandise[];
};

const SeasonPassProductActions: React.FC<SeasonPassProductActionsProps> = ({
  isChangingToProductsPage,
  merchandises,
}) => {
  const { styles } = useContext(ThemeContext);
  const { formatMessage, locale } = useIntl();
  const {
    isLoading,
    isCreating,
    isAddingLines,
    isRemovingLines,
    update,
    cartItems,
  } = useContext(CartContext);

  const [activeModal, setActiveModal] = useState<
    "application" | "renewal" | null
  >(null);
  const [editData, setEditData] = useState<
    SeasonCardApplicationEditData | undefined
  >(undefined);
  const [initialFields, setInitialFields] =
    useState<SeasonCardApplicationFields>(defaultInitialValues);
  const [initialPhotoId, setInitialPhotoId] = useState<string | undefined>(
    undefined,
  );

  const [selectedVariant, setSelectedVariant] =
    useState<ExtendedCartMerchandise>(merchandises[0]);

  const actingOnProduct =
    isLoading || isCreating || isAddingLines || isRemovingLines;

  const handleVariantChange = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const merchandise = merchandises.find(
        merchandise => merchandise.id === target.value,
      );
      if (merchandise) {
        setSelectedVariant(merchandise);
      }
    },
    [merchandises],
  );

  const isRenewalSelected = useMemo(
    () =>
      selectedVariant.selectedOptions.some(
        ({ name, value }) =>
          name === "Media" && value === SEASON_CARD_MEDIA[locale].renewal,
      ),
    [locale, selectedVariant.selectedOptions],
  );

  const closeModal: ModalFn = useCallback(
    modal => {
      if (modal === "application" && editData?.cartLineId) {
        setEditData(undefined);
      }
      setActiveModal(null);
      setInitialFields(defaultInitialValues);
      setInitialPhotoId(undefined);
    },
    [editData],
  );

  const handleRenewalData = useCallback(
    (
      {
        address: { street, city, postalCode },
        firstName,
        dateOfBirth,
        lastName,
        phoneNumber,
        email,
        photoId,
        guardianFirstName,
        guardianLastName,
      }: Paths.FetchSeasonCardHolder.Responses.$200,
      ticketId: string,
    ) => {
      setInitialFields({
        address: street,
        city,
        zip: postalCode,
        ticketId,
        dateOfBirth,
        firstName,
        lastName,
        phone: phoneNumber,
        email,
        guardianFirstName,
        guardianLastName,
        termsAccepted: false,
      });
      setInitialPhotoId(photoId);
      setActiveModal("application");
    },
    [],
  );

  const onSubmit = useCallback(
    async (
      {
        firstName,
        lastName,
        dateOfBirth,
        address,
        zip,
        city,
        email,
        phone,
        guardianFirstName,
        guardianLastName,
        ticketId,
      }: SeasonCardApplicationFields,
      photoId: string,
      cartLineId?: string,
    ) => {
      const attributes = [
        { key: SEASON_CARD_ATTRIBUTES.FIRST_NAME, value: firstName },
        { key: SEASON_CARD_ATTRIBUTES.LAST_NAME, value: lastName },
        { key: SEASON_CARD_ATTRIBUTES.DATE_OF_BIRTH, value: dateOfBirth },
        { key: SEASON_CARD_ATTRIBUTES.ADDRESS, value: address },
        { key: SEASON_CARD_ATTRIBUTES.POSTAL_CODE, value: zip },
        { key: SEASON_CARD_ATTRIBUTES.CITY, value: city },
        { key: SEASON_CARD_ATTRIBUTES.EMAIL, value: email },
        { key: SEASON_CARD_ATTRIBUTES.PHONE, value: phone },
        { key: SEASON_CARD_ATTRIBUTES.PHOTO_ID, value: photoId },
      ];

      if (guardianFirstName && guardianLastName) {
        attributes.push({
          key: SEASON_CARD_ATTRIBUTES.GUARDIAN_FIRST_NAME,
          value: guardianFirstName,
        });
        attributes.push({
          key: SEASON_CARD_ATTRIBUTES.GUARDIAN_LAST_NAME,
          value: guardianLastName,
        });
      }

      if (ticketId !== undefined && ticketId.length > 0) {
        attributes.push({
          key: SEASON_CARD_ATTRIBUTES.TICKET_ID,
          value: ticketId,
        });
      }

      const otherItems = cartLineId
        ? cartItems.filter(item => item.cartLineId !== cartLineId)
        : cartItems;

      await update({
        openMiniCart: true,
        newItems: [
          ...otherItems,
          {
            cartLineId,
            merchandise: selectedVariant,
            input: {
              quantity: 1,
              merchandiseId: selectedVariant.id,
              attributes,
            },
          },
        ],
      });
    },
    [update, cartItems, selectedVariant],
  );

  useEffect(() => {
    if (!editData) {
      const json = sessionStorage.getItem(SEASON_CARD_DATA_KEY);
      if (json) {
        try {
          const data: SeasonCardApplicationEditData = JSON.parse(json);
          const merchandise = merchandises.find(
            merchandise => merchandise.id === data.merchandiseId,
          );
          if (merchandise) {
            setSelectedVariant(merchandise);
          }
          setEditData({
            shouldOpenModal: data.shouldOpenModal,
            cartLineId: data.cartLineId,
            merchandiseId: data.merchandiseId,
            photoId: data.photoId,
            fields: { ...data.fields, termsAccepted: true },
          });
          sessionStorage.removeItem(SEASON_CARD_DATA_KEY);

          if (data.shouldOpenModal) {
            setActiveModal("application");
          }
        } catch (error) {
          Sentry.captureMessage(
            `Failed to process season pass edit data. Error: ${error}`,
          );
        }
      }
    }
  }, [cartItems, editData, merchandises]);

  return (
    <>
      <section
        className="pv4 mv4 bt b--moon-gray"
        data-testid={TEST_ID_SEASONPASS_ACTIONS}
      >
        <div className="mb3">
          {merchandises.reduce<JSX.Element[]>((elements, merchandise) => {
            const media = merchandise.selectedOptions.find(
              option => option.name === SelectedOption.MEDIA,
            );
            if (media) {
              elements.push(
                <SeasonPassProductVariant
                  key={merchandise.id}
                  isSelected={selectedVariant.id === merchandise.id}
                  selectVariant={handleVariantChange}
                  merchandise={merchandise}
                />,
              );
            }
            return elements;
          }, [])}
        </div>
      </section>
      <div className="flex flex-column flex-row-l justify-between">
        <button
          className={`${styles.secondaryButton} mb3 mb0-l order-2-l`}
          onClick={() =>
            setActiveModal(isRenewalSelected ? "renewal" : "application")
          }
          data-testid={TEST_ID_SEASONPASS_OPEN_APPLICATION_MODAL}
        >
          {actingOnProduct ? (
            <FormattedMessage id="status.updatingProductToCart" />
          ) : (
            <FormattedMessage id="product.buySeasonPass" />
          )}
        </button>
        <LocaleLink slug={formatMessage({ id: "appPath.products" })}>
          <a
            className={`${styles.primaryButton} order-1-l`}
            data-testid={TEST_ID_BACK_TO_SHOP_BUTTON}
          >
            {isChangingToProductsPage ? (
              <FormattedMessage id="product.loading" />
            ) : (
              <FormattedMessage id="product.backToShop" />
            )}
          </a>
        </LocaleLink>
      </div>
      {activeModal === "application" && (
        <SeasonPassProductModal
          open={activeModal === "application"}
          submit={onSubmit}
          onClose={() => closeModal("application")}
          initialFields={initialFields}
          initialPhotoId={initialPhotoId}
          editData={editData}
        />
      )}
      {activeModal === "renewal" && (
        <SeasonPassRenewalModal
          isOpen={activeModal === "renewal"}
          onRenewalData={handleRenewalData}
          onClose={() => closeModal("renewal")}
        />
      )}
    </>
  );
};

export default SeasonPassProductActions;
